'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import dynamic from 'next/dynamic'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { useUserId } from '@repo/cf-user/hooks/use-user-data'
import { isIosDevice, isSafari as isSafariBrowser } from 'utils/device'

import { CoinIcon } from '@web/components/icons/coin'
import { ReviewButton } from './review-button'
import { RatingInput } from './rating/input'
import { Rating } from './rating'
import { useShowCoins } from './use-show-coins'
import { useIsFirstDownload } from '@web/hooks/use-product-data'
import { useProductId } from '@web/app/[locale]/(web)/prod/[slug]/product-id-context'
interface ReviewCardProps {
  rating: number
  className: string
  readOnly?: boolean
}

const RatingModal = dynamic(() => import('./modal').then(mod => mod.RatingModal), {
  ssr: false,
})

export const COINS_REWARD = 1000

export function ReviewCard({ rating: initialRating, readOnly, className }: ReviewCardProps) {
  const t = useTranslations('prod.product_description_page.reviews')
  const productId = useProductId()
  const currentUserId = useUserId()
  const isAuthenticated = Boolean(currentUserId)
  const isIos = isIosDevice()
  const isSafari = isSafariBrowser()
  const { refetch: refetchLicenses, isLoading: isLoadingIsFirstDownload } = useIsFirstDownload(
    isIos && isSafari && Boolean(productId),
  )

  const { openModal } = useUserAuthModal()
  const [rating, setRating] = React.useState(initialRating)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const showCoins = useShowCoins()
  const title = initialRating === 0 ? t('post_first') : t(readOnly ? 'view' : 'post')

  React.useEffect(() => {
    setRating(initialRating)
  }, [initialRating])

  const onChangeAndOpen = async (value: number) => {
    if (!isLoadingIsFirstDownload) {
      await refetchLicenses()
    }
    if (isAuthenticated) {
      setRating(value)
      setIsModalOpen(true)
    } else {
      openModal()
    }
  }

  return (
    <div
      className={tw(
        'shadow-cf-elevation-light-1 rounded-4 3xl:flex-row flex flex-row flex-wrap items-center justify-start gap-4 p-6 min-[575px]:flex-col min-[575px]:items-start',
        className,
      )}
    >
      <div className="3xl:w-1/2 order-1 text-[18px] font-bold min-[575px]:mb-2 min-[575px]:text-xl">
        {title}
      </div>
      {showCoins ? (
        <div className="text-16 3xl:order-3 3xl:w-full order-3 flex items-center gap-1 font-semibold min-[575px]:text-lg">
          {t.rich('earn_coins', {
            coin: () => <CoinIcon className="inline-block text-[#F9A050]" />,
            number: COINS_REWARD.toLocaleString('en-US'),
          })}
        </div>
      ) : null}
      {readOnly ? (
        <Rating value={rating} className="3xl:order-4 order-2" readOnly />
      ) : (
        <RatingInput
          id="mainPageRating"
          value={rating}
          onChange={onChangeAndOpen}
          className="3xl:order-4 order-2"
          readOnly={readOnly}
        />
      )}
      <ReviewButton
        readOnly={readOnly}
        setIsModalOpen={async () => {
          if (!isLoadingIsFirstDownload) {
            await refetchLicenses()
          }
          isAuthenticated ? setIsModalOpen(true) : openModal()
        }}
        className="3xl:order-2 order-3"
      />
      <RatingModal
        readOnly={readOnly}
        rating={rating}
        setRating={setRating}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setRating(initialRating)
        }}
      />
    </div>
  )
}
